import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message, Modal } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import Highlighter from "react-highlight-words";
import CSVReader from "react-csv-reader";
import axios from "axios";
import AddApplication from "../applicationsManger/addApplication";
import ViewApplication from "../applicationsManger/viewApplication";
import EditApplication from "../applicationsManger/editApplication";
import "./applicationsV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const apiUrl = process.env.REACT_APP_API_Applications;
const bulkUploadEndpoint = "https://arvuc8x0kj.execute-api.us-east-1.amazonaws.com/dev/bulk-apps";
const sampleFileUrl = process.env.REACT_APP_AppsBulkUploadSampleFile;

const ApplicationsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [applications, setApplications] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [appId, setAppId] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewModalVisible, setviewModalVisible] = useState(false);
  const [editModalVisible, setEidtModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, [open, editModalVisible]);

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch applications");
      const data = await response.json();
      setApplications(data.body || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="applications-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="applications-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "applications-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewClick = (record) => {
    setviewModalVisible(true);
    setAppId(record.id);
  };

  const handleEditClick = (record) => {
    setEidtModalVisible(true);
    setAppId(record.id);
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${apiUrl}/${selectedRecord.id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        messageApi.success("Application deleted successfully");
        setApplications((prev) => prev.filter((r) => r.id !== selectedRecord.id));
      } else {
        messageApi.error("Failed to delete application");
      }
    } catch (error) {
      messageApi.error("Failed to delete application");
    }
    setDeleteModalVisible(false);
  };

  const handleAddClick = () => setOpen(true);

  const handleDownloadSample = () => {
    if (!sampleFileUrl) return;
    const anchor = document.createElement("a");
    anchor.href = sampleFileUrl;
    anchor.download = "sample.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleFileUpload = async (data) => {
    const formattedData = data.map((item) => ({
      app_type: item.app_type,
      name: item.name,
      visible: 1,
    }));
    try {
      const response = await axios.post(bulkUploadEndpoint, formattedData);
      if (response.data?.body === "Bulk Records Uploaded") {
        messageApi.success("Records uploaded successfully");
        fetchData();
      }
    } catch (error) {
      messageApi.error("Error uploading file");
    }
  };

  const columns = [
    {
      title: "#",
      key: "serial",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Application Name",
      dataIndex: "name",
      key: "name",
      width: "35%",
      ...getColumnSearchProps("name"),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "App Type",
      dataIndex: "app_type",
      key: "app_type",
      width: "25%",
      ...getColumnSearchProps("app_type"),
      sorter: (a, b) => (a.app_type || "").localeCompare(b.app_type || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "40%",
      render: (_, record) => (
        <div className="applications-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewClick(record)}
            className="applications-v2-btn-view"
          >
            View
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            className="applications-v2-btn-edit"
          >
            Edit
          </Button>
          <Button
            type="default"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
            danger
            className="applications-v2-btn-delete"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="applications-v2">
      {contextHolder}
      <div className="applications-v2-header">
        <h1 className="applications-v2-title">All Applications</h1>
        <div className="applications-v2-actions-row">
          <div className="applications-v2-bulk-actions">
            <CSVReader
              onFileLoaded={handleFileUpload}
              inputId="applications-csv-input"
              inputStyle={{ display: "none" }}
              parserOptions={{ header: true, skipEmptyLines: true }}
            />
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => document.getElementById("applications-csv-input")?.click()}
              className="applications-v2-btn-upload"
            >
              Upload CSV
            </Button>
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={handleDownloadSample}
              className="applications-v2-btn-download"
            >
              Download Sample
            </Button>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddClick}
            className="applications-v2-add-btn"
          >
            Add Application
          </Button>
        </div>
      </div>

      <div className="applications-v2-card">
        <Table
          columns={columns}
          dataSource={applications}
          loading={loading}
          rowKey="id"
          locale={{ emptyText: loading ? "Loading..." : "No applications yet" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} applications`,
          }}
          className="applications-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        className="applications-v2-delete-modal"
        width={420}
      >
        <p className="applications-v2-delete-text">
          Are you sure you want to delete <strong>{selectedRecord?.name}</strong>? This action cannot be undone.
        </p>
      </Modal>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="applications-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AddApplication setOpen={setOpen} />
      </Dialog>

      <Dialog
        open={viewModalVisible}
        onClose={() => setviewModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="applications-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <ViewApplication setOpen={setviewModalVisible} appId={appId} />
      </Dialog>

      <Dialog
        open={editModalVisible}
        onClose={() => setEidtModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="applications-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <EditApplication setOpen={setEidtModalVisible} id={appId} />
      </Dialog>
    </div>
  );
};

export default ApplicationsV2;
